/* The priority rules engine.
 *
 * Pure — no I/O, no clock of its own, no framework. It takes a rule list and a
 * facts object and returns a priority. That is deliberate: this same file moves
 * to api/src/shared/priorityRules.js when the backend is built, and the tests
 * written against it here go with it.
 *
 * Ordered list, FIRST MATCH WINS, default pinned last. The ticket stores which
 * rule fired so both the requester and IT can be shown why, which is the whole
 * argument for rules over a model: a rule can be pointed at.
 */

const FALLBACK_PRIORITY = 'P3';

/** Ops a condition may use. An unknown op returns false — never throws. */
function matches(condition, facts) {
  const actual = facts?.[condition.fact];
  const expected = condition.value;

  switch (condition.op) {
    case 'eq':
      return actual === expected;

    case 'neq':
      return actual !== expected;

    case 'in':
      return Array.isArray(expected) && expected.includes(actual);

    case 'gte':
      return typeof actual === 'number' && actual >= Number(expected);

    case 'lte':
      return typeof actual === 'number' && actual <= Number(expected);

    // `value` is ["HH:MM", "HH:MM"] and `actual` is minutes-past-local-midnight.
    // Windows that wrap past midnight (22:00–02:00) are handled by the OR arm.
    case 'time_between': {
      if (!Array.isArray(expected) || expected.length !== 2) return false;
      if (typeof actual !== 'number') return false;
      const from = toMinutes(expected[0]);
      const to = toMinutes(expected[1]);
      if (from === null || to === null) return false;
      return from <= to ? actual >= from && actual <= to : actual >= from || actual <= to;
    }

    default:
      return false;
  }
}

function toMinutes(hhmm) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm));
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

/**
 * @returns {{priority: string, ruleId: string|null, ruleName: string|null}}
 * Always returns a priority. A rule list that is empty, entirely disabled, or
 * missing its default still yields FALLBACK_PRIORITY rather than nothing — a
 * ticket with no priority is worse than a ticket with a dull one.
 */
export function evaluate(rules, facts) {
  const ordered = [...(rules || [])]
    .filter((r) => r && r.enabled !== false)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  for (const rule of ordered) {
    const conditions = rule.conditions || [];
    // ALL conditions on a rule must hold. An empty condition list matches
    // everything, which is exactly how the pinned default works.
    if (conditions.every((c) => matches(c, facts))) {
      return { priority: rule.priority, ruleId: rule.ruleId, ruleName: rule.name };
    }
  }

  return { priority: FALLBACK_PRIORITY, ruleId: null, ruleName: null };
}

/**
 * The whitelist of facts a rule may read. Built explicitly rather than by
 * spreading the ticket, so adding a form field can never silently become a
 * rule input nobody reviewed.
 */
export function factsFor({ ticket, requester, similarCount, now }) {
  const at = now instanceof Date ? now : new Date(now || Date.now());
  const form = ticket.formData || {};

  return {
    // 1 — the ticket
    category: ticket.categoryId,
    requestType: ticket.requestTypeId,
    brand: ticket.brand,
    // 'fault' or 'request'. Passed in by the caller rather than looked up here,
    // so this module stays pure — server-side the catalogue is a table, not an
    // import. A fault never carries neededBy and a request never carries
    // frequency/since/othersAffected, so a rule reading the wrong one for the
    // kind simply never matches. That is the intended behaviour, not a gap.
    kind: ticket.kind,
    frequency: form.frequency,
    since: form.since,
    othersAffected: form.othersAffected,
    neededBy: form.neededBy,

    // 2 — who raised it
    requesterDepartment: requester?.department,
    requesterRole: requester?.role,
    requesterBrand: requester?.brand,
    requesterIsLeadership: Boolean(requester?.isLeadership),

    // 3 — timing (minutes past local midnight, plus the weekday)
    raisedAtLocal: at.getHours() * 60 + at.getMinutes(),
    raisedOnWeekday: at.getDay(),

    // 4 — how many are affected
    similarCount: Number(similarCount) || 0,
  };
}

/** Facts a rule author can pick from, for the admin rule builder. */
export const FACT_CATALOG = [
  { fact: 'category',              label: 'Category',                  family: 'The ticket' },
  { fact: 'requestType',           label: 'Request type',              family: 'The ticket' },
  { fact: 'kind',                  label: 'Fault or request',          family: 'The ticket' },
  { fact: 'brand',                 label: 'Brand',                     family: 'The ticket' },
  { fact: 'frequency',             label: 'How often it happens',      family: 'Faults only' },
  { fact: 'since',                 label: 'When it started',           family: 'Faults only' },
  { fact: 'othersAffected',        label: 'Who else is affected',      family: 'Faults only' },
  { fact: 'neededBy',              label: 'When it is needed by',      family: 'Requests only' },
  { fact: 'requesterDepartment',   label: 'Requester department',      family: 'Who raised it' },
  { fact: 'requesterRole',         label: 'Requester role',            family: 'Who raised it' },
  { fact: 'requesterIsLeadership', label: 'Requester is exec or GM',   family: 'Who raised it' },
  { fact: 'raisedAtLocal',         label: 'Time of day raised',        family: 'Timing' },
  { fact: 'raisedOnWeekday',       label: 'Day of week raised',        family: 'Timing' },
  { fact: 'similarCount',          label: 'Similar tickets in an hour', family: 'How many are affected' },
];
